import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../../util/reactIntl';
import {
  Form,
  Heading,
  H3,
  PrimaryButton,
  H2,
  FieldNumberInput,
  FieldTextInput,
  FieldQuantityInput,
} from '../../../../../components';
import FieldTimeZoneSelect from '../FieldTimeZoneSelect';
import AvailabilityPlanEntries from './AvailabilityPlanEntries';

import css from './EditListingAvailabilityPlanForm.module.css';
import FieldSeatsInput from '../FieldSeatsInput/FieldSeatsInput';

/**
 * User might create entries inside the day of week in what ever order.
 * We sort them before submitting to Marketplace API
 */
const sortEntries = () => (a, b) => {
  if (a.startTime && b.startTime) {
    const aStart = Number.parseInt(a.startTime.split(':')[0]);
    const bStart = Number.parseInt(b.startTime.split(':')[0]);
    return aStart - bStart;
  }
  return 0;
};

/**
 * Handle submitted values: sort entries within the day of week
 * @param {Redux Thunk} onSubmit promise fn.
 * @param {Array<string>} weekdays ['mon', 'tue', etc.]
 */
const submit = (onSubmit, weekdays) => values => {
  const sortedValues = weekdays.reduce(
    (submitValues, day) => {
      return submitValues[day]
        ? {
            ...submitValues,
            [day]: submitValues[day].sort(sortEntries()),
          }
        : submitValues;
    },
    { ...values }
  );

  onSubmit(sortedValues);
};

/**
 * Hidden input field
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.name - the name of the form field/input
 * @returns {JSX.Element} component rendering a hidden form field.
 */
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden">
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

/**
 * @typedef {'sun'|'mon'|'tue'|'wed'|'thu'|'fri'|'sat'} Weekday
 */

/**
 * Create and edit availability plan of the listing.
 * This is essentially the weekly schedule.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string?} props.formId
 * @param {Object} props.form form API from React Final Form
 * @param {Function} props.handleSubmit
 * @param {boolean} props.inProgress
 * @param {string} props.listingTitle
 * @param {Array<Weekday>} props.weekdays
 * @param {boolean} props.useFullDays
 * @param {boolean} props.useMultipleSeats
 * @param {'hour'|'day'|'night'} props.unitType
 * @param {boolean} props.fetchErrors
 * @param {Object|null} props.fetchErrors.updateListingError
 * @param {Object} props.values form's values
 * @returns {JSX.Element} containing form that allows adding availability exceptions
 */
const EditListingAvailabilityPlanForm = props => {
  const intl = useIntl();
  const { onSubmit, ...restOfprops } = props;
  return (
    <FinalForm
      {...restOfprops}
      onSubmit={submit(onSubmit, props.weekdays)}
      mutators={{
        ...arrayMutators,
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          form: formApi,
          handleSubmit,
          inProgress,
          listingTitle,
          weekdays,
          useFullDays,
          useMultipleSeats,
          unitType,
          fetchErrors,
          values,
        } = fieldRenderProps;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;

        const concatDayEntriesReducer = (entries, day) =>
          values[day] ? entries.concat(values[day]) : entries;
        const hasUnfinishedEntries = !!weekdays
          .reduce(concatDayEntriesReducer, [])
          .find(e => !e.startTime || !e.endTime);

        const { updateListingError } = fetchErrors || {};

        const isMinSeatsNotValid = parseInt(values?.minSeats) > parseInt(values?.seats)

        const submitDisabled = submitInProgress || hasUnfinishedEntries || isMinSeatsNotValid;

        return (
          <Form id={formId} className={classes} onSubmit={handleSubmit}>
            <H2 as="h2" className={css.heading}>
              <FormattedMessage
                id="EditListingAvailabilityPlanForm.title"
                values={{ listingTitle }}
              />
            </H2>
            <Heading as="h3" rootClassName={css.subheading}>
              <FormattedMessage id="EditListingAvailabilityPlanForm.timezonePickerTitle" />
            </Heading>

            <div className={css.timezonePicker}>
              <FieldTimeZoneSelect
                id="timezone"
                name="timezone"
                selectClassName={css.timeZoneSelect}
                rootClassName={css.timeZoneField}
              />
            </div>

            <Heading as="h3" rootClassName={css.subheading}>
              <FormattedMessage id={`EditListingAvailabilityPlanForm.${useMultipleSeats ? 'minSeatsTitle' : 'privateMinSeatsTitle'}`} />
            </Heading>

            {useMultipleSeats ? (
              <div className={css.seatsHolder}>
                <div className={css.seatsRow}>
                  <label htmlFor="seats">
                    <FormattedMessage id="EditListingAvailabilityPlanForm.seatsLabel" />
                  </label>

                  <FieldQuantityInput
                    id={`seats`}
                    name={`seats`}
                    type="number"
                    min={1}
                    max={100000}
                    initialValue={values?.seats || 1}
                    className={css.field}
                  />
                </div>

                <div className={css.seatsRow}>
                  <label htmlFor="minSeats">
                    <FormattedMessage id="EditListingAvailabilityPlanForm.minSeatsLabel" />
                  </label>

                  <FieldQuantityInput
                    id={`minSeats`}
                    name={`minSeats`}
                    type="number"
                    min={1}
                    max={values?.seats || 1000000}
                    initialValue={values?.minSeats || 1}
                    className={css.field}
                  />
                </div>
                {isMinSeatsNotValid && (
                  <div className={css.error}>
                    <FormattedMessage id="EditListingAvailabilityPlanForm.seatsError" />
                  </div>
                )}
              </div>
            ) : (
              <div className={css.seatsHolder}>
                <div className={css.seatsRow}>
                  <label htmlFor="seats">
                    <FormattedMessage id="EditListingAvailabilityPlanForm.privateSeatsLabel" />
                  </label>

                  <FieldQuantityInput
                    id={`maxSeats`}
                    name={`maxSeats`}
                    type="number"
                    min={1}
                    max={100000}
                    initialValue={values?.maxSeats || 1}
                    className={css.field}
                  />
                  <FieldHidden name={`seats`} value={1} />
                </div>
              </div>
            )}

            <Heading as="h3" rootClassName={css.subheading}>
              <FormattedMessage id="EditListingAvailabilityPlanForm.hoursOfOperationTitle" />
            </Heading>
            <div className={css.week}>
              {weekdays.map(w => {
                return (
                  <AvailabilityPlanEntries
                    dayOfWeek={w}
                    useFullDays={useFullDays}
                    useMultipleSeats={useMultipleSeats}
                    unitType={unitType}
                    key={w}
                    values={values}
                    formApi={formApi}
                    intl={intl}
                  />
                );
              })}
            </div>

            <div className={css.submitButton}>
              {updateListingError ? (
                <p className={css.error}>
                  <FormattedMessage id="EditListingAvailabilityPlanForm.updateFailed" />
                </p>
              ) : null}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="EditListingAvailabilityPlanForm.saveSchedule" />
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default EditListingAvailabilityPlanForm;
